/**
 * Add / Edit customer modal.
 *
 * Renders nothing when `open={false}` so there's no work going on in the
 * background. When `customer` is supplied the modal opens in EDIT mode
 * (title, button label, and PUT semantics); otherwise it opens in ADD mode.
 *
 * Validation:
 *   • At least one of phone or email is required (matches the backend's
 *     400 BadRequest contract).
 *   • Email must be a plausible address.
 *   • Phone is best-effort E.164-ish.
 *
 * The caller passes `onSave(payload, mode)` and decides whether to call
 * `addCustomer` or `updateCustomer`. The modal closes itself on success.
 */
import { useEffect, useMemo, useState } from 'react';

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  tags: '',
  vip: false,
  fleetAccountId: '',
};

function friendlyError(err) {
  const status = err?.response?.status;
  const raw=
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  '';

  if (!err?.response && /network|fetch/i.test(raw)) {
    return "Couldn't reach the server. Check your connection and try again.";
  }

  if (status === 409 || /duplicate|already exists|conflict/i.test(raw)) {
    return 'A customer with this phone or email already exists.';
  }

  if (status === 400 || status === 422) {
    return raw || 'Please double-check the highlighted fields and try again.';
  }

  if (status === 401 || status === 403) {
    return "You don't have permission to do that. Try signing in again.";

  }

  if (status >= 500) {
    return "Something went wrong on our end - it's not you. Please try again in a moment.";

  }

  return raw || 'Save failed. Please try again.';
}

export default function CustomerModal({ open, mode = 'create', customer, onClose, onSave }) {
  const isEdit = mode === 'edit' && !!customer;
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState({});


  // Re-seed the form whenever the modal opens or the target customer changes.
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        name:           customer.name           || '',
        phone:          customer.phone === '—' ? '' : (customer.phone || ''),
        email:          customer.email === '—' ? '' : (customer.email || ''),
        tags:           Array.isArray(customer.tags) ? customer.tags.join(', ') : '',
        vip:            (customer.tags || []).includes('VIP'),
        fleetAccountId: customer.fleetAccountId || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError(null);
    setBusy(false);
    setTouched({});
  }, [open, isEdit, customer]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const trimmedPhone = form.phone.trim();
  const trimmedEmail = form.email.trim();
  const trimmedName  = form.name.trim();

  const canSubmit = useMemo(() => {
    if (busy) return false;
    if (!trimmedPhone && !trimmedEmail) return false;
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) return false;
    return true;
  }, [busy, trimmedPhone, trimmedEmail]);

  const emailInvalid = touched.email && trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const contactMissing = (touched.phone || touched.email) && !trimmedPhone && !trimmedEmail;
  const markTouched = (key) => setTouched((t) => ({ ...t, [key]: true }));


  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) {
      setTouched({ name: true, phone: true, email: true, tags: true, fleetAccountId: true });
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
      const payload = {
        name:           trimmedName,
        phone:          trimmedPhone,
        email:          trimmedEmail,
        tags,
        vip:            form.vip,
        fleetAccountId: form.fleetAccountId.trim() || null,
      };
      await onSave?.(payload, isEdit ? 'edit' : 'create');
      onClose?.();
    } catch (err) {
    
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cust-modal-title"
      onClick={onClose}
      className="modal-backdrop"
    >
      <form
        className="modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="modal-header">
          <div>
            <div id="cust-modal-title" style={{ fontSize: 16, fontWeight: 600 }}>
              {isEdit ? `Edit · ${customer?.name || 'Customer'}` : 'New customer'}
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              {isEdit
                ? 'Update contact details, fleet account, or VIP status.'
                : 'Add a prospect or owner. You can enrich the record afterwards.'}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-field">
            <label htmlFor="cust-name">Full name</label>
            <input
              id="cust-name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              onBlur={() => markTouched('name')}
              placeholder="e.g. Karan Sharma"
              autoFocus
            />
          </div>

          <div className="grid-2" style={{ gap: 12 }}>
            <div className="form-field">
              <label htmlFor="cust-phone">Phone (E.164)</label>
              <input
                id="cust-phone"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                onBlur={() => markTouched('phone')}
                placeholder="+61412345678"
                inputMode="tel"
                aria-invalid = {contactMissing || undefined}
                style={contactMissing ? { borderColor: 'var(--danger)' } : undefined}
              />
            </div>

            <div className="form-field">
              <label htmlFor="cust-email">Email</label>
              <input
                id="cust-email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                onBlur={() => markTouched('email')}
                placeholder="name@example.com"
                inputMode="email"
                aria-invalid = {(emailInvalid || contactMissing) || undefined}
                aria-describedby = {emailInvalid ? 'cust-email-err' : undefined}
                style = {(emailInvalid || contactMissing) ? { borderColor: 'var(--danger) '} : undefined}
              />

              {emailInvalid && (
                <span id = "cust-email-err" className = "mono text-xs" style = {{ color: 'var(--danger' }}>
                  Enter a valid email address, e.g. name@example.com
                </span>
              )}
            </div>
          </div>

          {contactMissing && !emailInvalid && (
            <div className = "mono text-xs" style = {{ color: 'var(--danger)', marginTop: -8}}>
              Add a phone number or email address so we can reach this customer.
            </div>
          )}

          <div className="form-field">
            <label htmlFor="cust-tags">Tags . optional (comma-separated)</label>
            <input
              id="cust-tags"
              value={form.tags}
              onChange={(e) => setField('tags', e.target.value)}
              placeholder="Lead, Hilux, Trade-in"
            />
          </div>

          <div className="grid-2" style={{ gap: 12, alignItems: 'end' }}>
            <div className="form-field">
              <label htmlFor="cust-fleet">Fleet account ID . optional</label>
              <input
                id="cust-fleet"
                value={form.fleetAccountId}
                onChange={(e) => setField('fleetAccountId', e.target.value)}
                placeholder="Optional"
              />
            </div>
            <label
              htmlFor="cust-vip"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 13,
                cursor: 'pointer',
                background: form.vip ? 'var(--accent-glow)' : 'transparent',
              }}
            >
              <input
                id="cust-vip"
                type="checkbox"
                checked={form.vip}
                onChange={(e) => setField('vip', e.target.checked)}
              />
              <span>VIP customer</span>
            </label>
          </div>

          {/* Validation hint / API error */}
          {!trimmedPhone && !trimmedEmail && (
            <div className="mono text-xs text-muted">
              At least one of phone or email is required.
            </div>
          )}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '10px 12px',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid var(--danger)',
              borderRadius: 6,
              color: 'var(--danger)',
              fontsize: 12,
              lineHeight: 1.5,
            }}>
               <span aria-hidden="true">⚠</span>
               <span>{error}</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="btn small primary"
            disabled={!canSubmit}
          >
            {busy
              ? (isEdit ? 'Saving…' : 'Creating…')
              : (isEdit ? 'Save changes' : 'Create customer')}
          </button>
        </div>
      </form>
    </div>
  );
}
